import { Modal } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import moment from 'moment';
import { toast } from 'react-toastify';
import { requestGetInviteJobMailTemps } from '../../../services/InviteJobMailTemplateService';
import { requestCandidates, getCandidateContactEmailToSend, getCandidateCareerDetail, getCandidateFlowDetail } from '../../../services/CandidateService';
import { requestSendCampaign, requestRetryCampaign } from '../../../services/MailService';
import { requestGetUserEmailList } from '../../../services/UserService';
import { MailTempType } from '../../email/common/Constant';
import { DVTJCUVAcquireCddParentTabIds } from '../../dvtjcuv/common/AcquireCddTabs';
import { mergeTemplateBody } from '../../../utils/CommonUtils';
import { htmlToPlainText } from '../../../utils/StringUtils';
import {
    knowStateLabel, readKnowStateFromFlowDetail, selectTemplateBody,
    isEligibleForIntroEmail, readSentIntroFromFlowDetail,
} from '../../../utils/MailTemplateUtils';
import { resolvePersonalization } from '../../../utils/PersonalizationUtils';
import EmailDraftContent from './EmailDraftContent';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';


// Shared by onClickNext (initial batch prefetch) and refreshCurrentCandidate (single-candidate
// refresh on tab focus) so both compute emails/subject/body the same way.
const buildCandidateEmailData = (candidate, contactContent, careerItems, template, flowContent) => {
    const adEmails = Array.isArray(contactContent?.email_to_send_ad) ? contactContent.email_to_send_ad : [];
    const contactEmails = adEmails.length > 0
        ? adEmails
        : (Array.isArray(contactContent?.email_to_send) ? contactContent.email_to_send : []);

    const careerEmails = (Array.isArray(careerItems) ? careerItems : [])
        .filter(c => c.work_status === '1' && c.company_email)
        .map(c => c.company_email);

    const emails = [...new Set([...contactEmails, ...careerEmails])].filter(Boolean);

    const personal = candidate?.personal || {};
    const location = candidate?.location || {};
    // Lấy từ /candidate/detail/flow — xem readKnowStateFromFlowDetail để biết vì sao
    // KHÔNG dùng candidate.flow_categories của /candidate/list/.
    const knowState = readKnowStateFromFlowDetail(flowContent);
    // [SPEC: TEST Gửi Email qua PM - DVTJCUV 4: EE9] R5 — tên UV lấy từ "Tên (email)"
    // (email_name), KHÔNG fallback sang full_name: Person thiếu email_name bị đẩy sang tab
    // "Thiếu thông tin" nên không bao giờ gửi ra email với chỗ tên bị trống.
    const displayName = personal.email_name || '';

    const { callerName, vocative } = resolvePersonalization(personal, location);
    const mergeVars = {
        caller_name_to_email: callerName,
        vocative,
        email_name: displayName,
        // {{Tên ứng viên}} map về key `full_name` trong MAIL_MERGE_FIELDS nên phải điền
        // email_name vào ĐÚNG key này. Không đổi label trong registry: template đã lưu chứa
        // chuỗi nhãn "Tên ứng viên", đổi label sẽ làm placeholder cũ fill rỗng.
        full_name: displayName,
    };

    return {
        emails,
        // [SPEC: TEST Gửi Email qua PM - DVTJCUV 4: EE9] R5-c — trả email_name ra ngoài để điều
        // kiện chia tab kiểm tra được: thiếu "Tên (email)" => tab "Thiếu thông tin", không gửi.
        email_name: displayName,
        // Subject soạn bằng SunEditor nên là HTML; header của email chỉ nhận text thuần.
        subject: htmlToPlainText(mergeTemplateBody(template?.subject || '', mergeVars)),
        body: mergeTemplateBody(selectTemplateBody(template, knowState), mergeVars),
        // [SPEC: TEST Gửi Email qua PM - DVTJCUV 5 - BS2] trả mergeVars ra ngoài để dựng body
        // của email giới thiệu (email2_instructor_body) khi gửi thêm — merge vars giống email chính.
        mergeVars,
    };
};

// [FIX: chống gửi trùng] Chữ ký của 1 lượt gửi — dùng để so sánh "batch sắp gửi" với "batch vừa
// gửi thành công gần nhất". Danh tính 1 recipient = (candidate_id, email, email_kind); sender đổi
// cũng tính là batch khác (cho phép gửi lại từ 1 địa chỉ khác một cách chủ động).
const computeSendSignature = (recipients, senderEmail) => JSON.stringify({
    sender: senderEmail,
    recipients: recipients
        .map(r => `${r.candidate_id}|${r.email}|${r.email_kind || ''}`)
        .sort(),
});

const DraftPreviewModal = (props) => {
    const {
        candidateIds,
        campaignId,
        isShow,
        onClose,
        parentTab,
        subTab,
        removeCandidateOutTabs,
        updateCandidatesInSendMails,
        // [SPEC: TEST Gửi Email qua PM - DVTJCUV 5 - BS2] gửi cho danh sách Rec hay UV — mặc
        // định false để không phá 2 call site chưa kịp truyền prop này.
        isRec = false,
    } = props;

    const [step, setStep] = useState(1);
    // [SPEC: TEST Gửi Email qua PM - DVTJCUV 5 - BS2]
    const [sendIntroEmail, setSendIntroEmail] = useState(false);
    const [templateList, setTemplateList] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [senderIdentities, setSenderIdentities] = useState([]);
    const [selectedSender, setSelectedSender] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [candidates, setCandidates] = useState([]);
    const [emailSelections, setEmailSelections] = useState({}); // { [candidateId]: string[] }
    const [candidateDataMap, setCandidateDataMap] = useState({}); // { [id]: { emails: string[], subject: string, body: string } }
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [activeTab, setActiveTab] = useState('valid'); // 'valid' | 'invalid'
    const [viewMode, setViewMode] = useState('preview'); // 'preview' | 'list'
    const [showSendTimeModal, setShowSendTimeModal] = useState(false);
    const [sendOption, setSendOption] = useState('now');
    const [sendTime, setSendTime] = useState(new Date());
    const [isSending, setIsSending] = useState(false);
    // [FIX: chống gửi trùng] Chữ ký của lượt gửi thành công gần nhất trong phiên mở modal này —
    // dùng để disable nút "Send Email" khi user bấm lại mà danh sách/nội dung gửi chưa đổi gì
    // (bug: gửi xong không reload vẫn tick lại checkbox/bấm Send Email => tạo campaign mới, gửi
    // trùng đúng batch vừa gửi). Tự reset khi danh sách/lựa chọn thực sự thay đổi (Loại/Xóa Person,
    // đổi email chọn, reload lại candidate, đổi sender...) vì signature khi đó sẽ khác đi.
    const [lastSentSignature, setLastSentSignature] = useState(null);
    const [modalParentTab, setModalParentTab] = useState(null);
    const [modalSubTab, setModalSubTab] = useState(null);

    useEffect(() => {
        setModalParentTab(parentTab);
        setModalSubTab(subTab);
    }, [parentTab, subTab]);


    useEffect(() => {
        if (isShow) {
            // [FIX: chống gửi trùng] Mở lại modal cho một `candidateIds` mới (kể cả trùng với lần
            // trước) là một phiên gửi mới — không nên bị khoá bởi lượt gửi thành công của lần mở
            // trước đó (component không unmount giữa các lần mở/đóng nên state cũ vẫn còn).
            setLastSentSignature(null);
            fetchTemplates();
            fetchSenderIdentities();
            if (candidateIds?.length > 0) {
                fetchAllCandidates();
            }
        }
    }, [isShow, candidateIds]);

    const fetchAllCandidates = async () => {
        try {
            const res = await requestCandidates({
                search_conditions: { candidate_ids: candidateIds }
            });
            setCandidates(res.content.candidates || []);
        } catch (err) {
            console.error('Fetch candidates error:', err);
        }
    };

    const fetchTemplates = async () => {
        try {
            // DraftPreviewModal is opened from two flows with different parentTab/subTab id
            // namespaces:
            // - RunJob (job-invite): templates have NULL parent_tab_id/sub_tab_id
            //   (InviteJobTemplateListManager.js never sets them) — filter by template_type only.
            // - DVTJCUV Acquire-Cdd: raw parentTab.id/subTab.id don't match the tab ids templates
            //   are stored under — translate to the real template-storage tab ids
            //   (DVTJCUVTabs.ACQUIRE_CDD_TAB.id / AcquireCddTabs.INVITE_TAB.id in
            //   pages/email/template/dvtjcuv/DVTJCUVTemplateListManager.js — hardcoded here to
            //   avoid importing that file, which pulls in suneditor-react), no template_type filter.
            const isDVTJCUVAcquireCddFlow = parentTab?.id === DVTJCUVAcquireCddParentTabIds.EMAIL_TAB;
            const search_conditions = isDVTJCUVAcquireCddFlow
                ? {
                    parent_tab_id: 'dvtjcuv-acquire-cdd-tab',
                    sub_tab_id: 'acquire-invite-tab',
                }
                : {
                    // template_type: [MailTempType.SEMI_FULL, MailTempType.FULL],
                };

            const res = await requestGetInviteJobMailTemps({ search_conditions });
            const items = res.content.mail_templates || [];
            setTemplateList(items);
        } catch (err) {
            console.error('Fetch templates error:', err);
        }
    };

    const fetchSenderIdentities = async () => {
        try {
            // Sender lay tu emails da setting san cua user dang login (bang UserEmail),
            // khong con lay tu SES verified identities.
            // Goi KHONG truyen user_id -> backend user_email_list roi vao nhanh
            // filter(user=request.user) nen luon tra emails cua chinh user dang login,
            // ke ca khi user do la superuser (nhanh admin chi kich hoat khi CO user_id).
            const res = await requestGetUserEmailList();
            // Khac endpoint SES cu (raw { identities: [...] }, mang string), endpoint nay
            // ENVELOPED: { content: { user_emails: [{ id, email, ... }] } } — mang object.
            // Map ve mang string de giu nguyen contract cua <select> / selectedSender.
            const emails = (res?.content?.user_emails || [])
                .map(item => item.email)
                .filter(Boolean); // UserEmail.email la null=True, blank=True
            setSenderIdentities(emails);
        } catch (err) {
            console.error('Fetch sender identities error:', err);
        }
    };

    const tab1Candidates = candidates.filter(c => {
        const id = String(c.general?.id);
        const data = candidateDataMap[id];
        // [SPEC: ... DVTJCUV 4: EE9] R5-c — thiếu "Tên (email)" cũng là thiếu thông tin.
        return data && data.emails?.length > 0 && data.subject?.trim() && data.body?.trim()
            && data.email_name?.trim();
    });

    const tab2Candidates = candidates.filter(c => {
        const id = String(c.general?.id);
        const data = candidateDataMap[id];
        return !data || !(data.emails?.length > 0) || !data.subject?.trim() || !data.body?.trim()
            || !data.email_name?.trim();
    });

    // Mỗi email đã chọn của mỗi candidate ở tab "Đủ thông tin" trở thành 1 recipient riêng, mang
    // theo đúng subject/body cá nhân hóa của candidate đó. Tách thành hàm riêng (thay vì build
    // inline trong onConfirmSend) để dùng lại được cho việc tính signature chống gửi trùng bên dưới.
    const buildRecipients = () => {
        const recipients = tab1Candidates.flatMap(c => {
            const id = String(c.general?.id);
            const data = candidateDataMap[id];
            const emails = emailSelections[id] || [];
            return emails.filter(Boolean).map(email => ({
                email,
                candidate_id: c.general?.id,
                subject: data?.subject || '',
                body: data?.body || '',
            }));
        });

        // [SPEC: TEST Gửi Email qua PM - DVTJCUV 5 - BS2] Checkbox "Gửi EMAIL GIỚI THIỆU..." —
        // chỉ áp dụng luồng gửi campaign MỚI (không áp dụng khi campaignId có giá trị — retry).
        if (sendIntroEmail && !campaignId) {
            tab1Candidates.forEach(c => {
                const id = String(c.general?.id);
                const data = candidateDataMap[id];
                const emails = emailSelections[id] || [];
                const knowState = c.flow_categories?.candidate_know_state;
                const alreadySentIntro = readSentIntroFromFlowDetail(c.flow_categories, isRec);
                if (!isEligibleForIntroEmail(knowState, alreadySentIntro)) return;

                const introBody = mergeTemplateBody(selectedTemplate?.email2_instructor_body || '', data?.mergeVars || {});
                emails.filter(Boolean).forEach(email => {
                    recipients.push({
                        email,
                        candidate_id: c.general?.id,
                        subject: data?.subject || '',
                        body: introBody,
                        email_kind: isRec ? 'INTRO_REC' : 'INTRO_UV',
                    });
                });
            });
        }

        return recipients;
    };

    // [FIX: chống gửi trùng] So sánh batch SẮP gửi (nếu bấm Send Email ngay bây giờ) với batch
    // vừa gửi thành công gần nhất trong phiên mở modal này — giống hệt thì disable nút Send Email,
    // tránh tạo campaign mới gửi trùng khi user quên reload (hoặc bấm lại trong lúc background
    // task `process_tasks` chưa kịp xử lý xong lượt gửi trước).
    const pendingSendSignature = computeSendSignature(buildRecipients(), selectedSender);
    const alreadySentSameBatch = lastSentSignature !== null && lastSentSignature === pendingSendSignature;

    const activeCandidatesList = activeTab === 'valid' ? tab1Candidates : tab2Candidates;
    const activeCandidateIdsList = activeCandidatesList.map(c => String(c.general?.id));

    const safeIndex = Math.min(currentIndex, Math.max(0, activeCandidateIdsList.length - 1));
    const currentCandidateId = activeCandidateIdsList[safeIndex];
    const currentCandidate = activeCandidatesList[safeIndex];

    const templateOptions = templateList.map(t => ({ value: t.id, label: t.name, data: t }));
    const selectedOption = selectedTemplate
        ? { value: selectedTemplate.id, label: selectedTemplate.name, data: selectedTemplate }
        : null;

    const onSelectTemplate = (option) => {
        setSelectedTemplate(option ? option.data : null);
    };

    const onClickNext = async () => {
        if (step === 1 && selectedTemplate) {
            setLoadingDetails(true);
            try {
                const dataMap = {};
                const initialSelections = {};
                const knowStateMap = {};
                const introSentMap = {};
                await Promise.all(candidates.map(async (candidate) => {
                    const candidateId = candidate.general?.id;

                    const [contactRes, careerRes, flowRes] = await Promise.all([
                        getCandidateContactEmailToSend(candidateId).catch(() => ({ content: {} })),
                        getCandidateCareerDetail(candidateId).catch(() => ({ content: [] })),
                        getCandidateFlowDetail(candidateId).catch(() => ({ content: {} }))
                    ]);

                    const data = buildCandidateEmailData(candidate, contactRes?.content, careerRes?.content, selectedTemplate, flowRes?.content);

                    dataMap[candidateId] = data;
                    initialSelections[candidateId] = data.emails;
                    knowStateMap[candidateId] = readKnowStateFromFlowDetail(flowRes?.content);
                    // [SPEC: TEST Gửi Email qua PM - DVTJCUV 5 - BS2]
                    introSentMap[candidateId] = {
                        sent_intro_email_uv: readSentIntroFromFlowDetail(flowRes?.content, false),
                        sent_intro_email_rec: readSentIntroFromFlowDetail(flowRes?.content, true),
                    };
                }));

                // Ghi know_state ngược vào candidate: EmailDraftContent tự dựng lại body để
                // preview, nếu không có field này thì preview sẽ lệch với body thực sự gửi đi.
                setCandidates((prev) => prev.map((c) => ({
                    ...c,
                    flow_categories: {
                        ...c.flow_categories,
                        candidate_know_state: knowStateMap[c.general?.id],
                        ...introSentMap[c.general?.id],
                    },
                })));
                setCandidateDataMap(dataMap);
                setEmailSelections(initialSelections);
            } catch (err) {
                console.error('Error prefetching candidate details:', err);
            } finally {
                setLoadingDetails(false);
            }
            setCurrentIndex(0);
            setStep(2);
            setActiveTab('valid');
        } else if (step === 2 && currentIndex < activeCandidateIdsList.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const onClickBack = () => {
        if (step === 2) {
            if (currentIndex > 0) {
                setCurrentIndex(currentIndex - 1);
            } else {
                setStep(1);
            }
        }
    };

    const onExcludeFromSend = async (candidateId) => {
        if (!candidateId) return;
        if (updateCandidatesInSendMails) {
            await updateCandidatesInSendMails([candidateId], false);
        }
        setCandidates((prev) => prev.filter((c) => String(c.general?.id) !== String(candidateId)));
    };

    const onConfirmSend = async () => {
        if (sendOption === 'later' && sendTime <= new Date()) {
            toast.error('Thời gian gửi phải ở trong tương lai, vui lòng chọn lại (không được ở quá khứ).');
            return;
        }

        const recipients = buildRecipients();

        if (recipients.length === 0) {
            toast.error('Không có email người nhận nào để gửi.');
            return;
        }

        // [FIX: chống gửi trùng] Chốt signature TRƯỚC khi gọi API — cùng chọn lựa hiện tại
        // (candidate/email/checkbox/sender) mà bấm Send lần nữa (kể cả trong lúc request đang
        // chạy, trước khi setIsSending kịp disable nút bên trong modal con) sẽ bị chặn ngay khi
        // request thành công, không phụ thuộc timing của background task xử lý campaign.
        const signatureToRecord = computeSendSignature(recipients, selectedSender);

        setIsSending(true);
        try {
            // apiRequest's response interceptor already unwraps axios's response.data
            if (campaignId) {
                // TASK-UPDATECHITIETCAMPAGIN: retry — update campaign hiện tại (ghi đè recipients
                // FAILED), KHÔNG tạo campaign mới. scheduled_send_time không áp dụng cho retry.
                const data = await requestRetryCampaign(campaignId, {
                    sender_email: selectedSender,
                    candidate_ids: candidateIds,
                    recipients,
                });
                if (data && data.campaign_id) {
                    toast.success(`Đã gửi lại ${data.total_recipients} email cho campaign #${data.campaign_id}.`);
                    setShowSendTimeModal(false);
                    setLastSentSignature(signatureToRecord);
                }
            } else {
                // Tên campaign phải unique (constraint ở backend) nên gắn thêm timestamp
                const data = await requestSendCampaign({
                    name: `${selectedTemplate?.name || 'Campaign'} - ${moment().format('YYYYMMDD-HHmmss')}`,
                    sender_email: selectedSender,
                    recipients,
                    ...(sendOption === 'later' ? { scheduled_send_time: sendTime.toISOString() } : {}),
                });
                if (data && data.campaign_id) {
                    const message = sendOption === 'later'
                        ? `Đã lên lịch gửi campaign #${data.campaign_id} (${data.total_recipients} email) vào ${moment(sendTime).format('DD/MM/YYYY HH:mm:ss')}.`
                        : `Đã tạo campaign #${data.campaign_id} và đưa ${data.total_recipients} email vào hàng đợi gửi.`;
                    toast.success(message);
                    setShowSendTimeModal(false);
                    setLastSentSignature(signatureToRecord);
                }
            }
        } finally {
            setIsSending(false);
        }
    };

    const onRemoveFromTab = async (candidateId) => {
        if (!candidateId) return;
        if (window.confirm('Bạn có chắc chắn muốn xóa (các) Person này khỏi các Tab không?')) {
            if (removeCandidateOutTabs) {
                await removeCandidateOutTabs(undefined, [candidateId]);
            }
            setCandidates((prev) => prev.filter((c) => String(c.general?.id) !== String(candidateId)));
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setCurrentIndex(0);
    };

    // Person ID link opens the candidate detail page in a new tab. Refreshing must be an explicit
    // user action (reload button next to "Send Email") — NOT triggered automatically by the tab
    // regaining window focus, and NOT triggered by Next/Back navigation between candidates. Only
    // the candidate currently being viewed is refetched (not the whole list).
    const refreshCurrentCandidate = async () => {
        if (!currentCandidateId || !selectedTemplate) return;
        try {
            const [candRes, contactRes, careerRes, flowRes] = await Promise.all([
                requestCandidates({ search_conditions: { candidate_ids: [Number(currentCandidateId)] } }),
                getCandidateContactEmailToSend(currentCandidateId).catch(() => ({ content: {} })),
                getCandidateCareerDetail(currentCandidateId).catch(() => ({ content: [] })),
                getCandidateFlowDetail(currentCandidateId).catch(() => ({ content: {} })),
            ]);

            const updatedCandidate = candRes?.content?.candidates?.[0];
            if (!updatedCandidate) return;

            const data = buildCandidateEmailData(updatedCandidate, contactRes?.content, careerRes?.content, selectedTemplate, flowRes?.content);

            // Giữ nguyên know_state vừa lấy được, xem chú thích ở onClickNext.
            const candidateWithKnowState = {
                ...updatedCandidate,
                flow_categories: {
                    ...updatedCandidate.flow_categories,
                    candidate_know_state: readKnowStateFromFlowDetail(flowRes?.content),
                    // [SPEC: TEST Gửi Email qua PM - DVTJCUV 5 - BS2]
                    sent_intro_email_uv: readSentIntroFromFlowDetail(flowRes?.content, false),
                    sent_intro_email_rec: readSentIntroFromFlowDetail(flowRes?.content, true),
                },
            };

            setCandidates((prev) => prev.map((c) =>
                String(c.general?.id) === String(currentCandidateId) ? candidateWithKnowState : c
            ));
            setCandidateDataMap((prev) => ({ ...prev, [currentCandidateId]: data }));
            setEmailSelections((prev) => ({ ...prev, [currentCandidateId]: data.emails }));
        } catch (err) {
            console.error('Refresh candidate error:', err);
        }
    };

    return (
        <>
            <Modal show={isShow} fullscreen onHide={onClose} animation={false}>
                <Modal.Header className="d-flex align-items-center justify-content-between">
                    <Modal.Title>Draft Preview</Modal.Title>
                    <div>
                        {step === 2 && (
                            <Button
                                variant="outline-primary"
                                className="mr-3"
                                onClick={() => setViewMode(viewMode === 'preview' ? 'list' : 'preview')}
                            >
                                {viewMode === 'preview' ? (
                                    <><i className="fas fa-list mr-1" /> Xem danh sách</>
                                ) : (
                                    <><i className="fas fa-eye mr-1" /> Xem chi tiết (Preview)</>
                                )}
                            </Button>
                        )}
                        <Button variant="secondary" onClick={onClose}>Close</Button>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {step === 1 && (
                        <div style={{ height: '90vh', overflowY: 'auto' }}>
                            <h5>Chọn Template</h5>
                            <div className="w-100">
                                <Select
                                    options={templateOptions}
                                    value={selectedOption}
                                    onChange={onSelectTemplate}
                                    placeholder="Chọn template..."
                                    isClearable
                                />
                            </div>
                            <div className="mt-3">
                                <h5>Chọn Sender's email</h5>
                                <select
                                    data-testid="sender-select"
                                    className="form-control"
                                    value={selectedSender}
                                    onChange={(e) => setSelectedSender(e.target.value)}
                                >
                                    <option value="">Chọn sender...</option>
                                    {senderIdentities.map((identity) => (
                                        <option key={identity} value={identity}>{identity}</option>
                                    ))}
                                </select>
                                <small data-testid="sender-ses-warning" className="text-warning d-block mt-1">
                                    ⚠️ Email phải được verify trong AWS SES mới gửi được campaign.
                                </small>
                                {senderIdentities.length === 0 &&
                                    <small data-testid="sender-empty-hint" className="text-danger d-block mt-1">
                                        Bạn chưa setting email nào. Vào Mail → Your mail để thêm.
                                    </small>
                                }
                            </div>
                            <div className="mt-3">
                                <Button
                                    variant="primary"
                                    disabled={!selectedTemplate}
                                    onClick={onClickNext}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div style={{ height: '90vh', overflowY: 'auto' }}>
                            {loadingDetails ? (
                                <div className="p-3 text-muted">Đang xử lý dữ liệu...</div>
                            ) : (
                                <>
                                    {/* Tab Selector */}
                                    <ul className="nav nav-tabs mb-3" role="tablist">
                                        <li className="nav-item">
                                            <a
                                                className={`nav-link ${activeTab === 'valid' ? 'active' : ''}`}
                                                data-toggle="tab"
                                                href="#valid"
                                                onClick={(e) => { e.preventDefault(); handleTabChange('valid'); }}
                                            >
                                                Đủ thông tin ({tab1Candidates.length})
                                            </a>
                                        </li>
                                        <li className="nav-item">
                                            <a
                                                className={`nav-link ${activeTab === 'invalid' ? 'active' : ''}`}
                                                data-toggle="tab"
                                                href="#invalid"
                                                onClick={(e) => { e.preventDefault(); handleTabChange('invalid'); }}
                                            >
                                                Thiếu thông tin ({tab2Candidates.length})
                                            </a>
                                        </li>
                                    </ul>

                                    {viewMode === 'list' ? (
                                        activeCandidateIdsList.length === 0 ? (
                                            <div className="alert alert-info">Không có person nào trong tab này.</div>
                                        ) : (
                                            <div className="card">
                                                <div className="card-body table-responsive p-0">
                                                    <table className="table table-hover table-bordered text-nowrap">
                                                        <thead className="bg-light text-center">
                                                            <tr>
                                                                <th>No.</th>
                                                                <th>Person ID</th>
                                                                <th>Tên (email)</th>
                                                                <th>Displayed Company</th>
                                                                <th>Displayed Title</th>
                                                                <th>Email nhận</th>
                                                                <th>Subject (Tiêu đề)</th>
                                                                <th>Thao tác</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {activeCandidatesList.map((candidate, idx) => {
                                                                const id = String(candidate.general?.id);
                                                                const emails = emailSelections[id] || [];
                                                                const data = candidateDataMap[id];
                                                                return (
                                                                    <tr key={id}>
                                                                        <td className="text-center">{idx + 1}</td>
                                                                        <td className="text-center">
                                                                            <a
                                                                                href={`/candidate/detail/${id}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                            >
                                                                                {id}
                                                                            </a>
                                                                        </td>
                                                                        {/* [SPEC: ... DVTJCUV 4: EE9] R5-a */}
                                                                        <td><strong>{candidate.personal?.email_name || '-'}</strong></td>
                                                                        <td>{candidate.general?.displayed_company || '-'}</td>
                                                                        <td>{candidate.general?.displayed_title || '-'}</td>
                                                                        <td>
                                                                            {emails.length > 0 ? (
                                                                                emails.map(email => (
                                                                                    <span key={email} className="badge badge-secondary mr-1">{email}</span>
                                                                                ))
                                                                            ) : (
                                                                                <span className="text-danger"><em>(Trống)</em></span>
                                                                            )}
                                                                        </td>
                                                                        <td>{data?.subject || <span className="text-danger"><em>(Trống)</em></span>}</td>
                                                                        <td className="text-center">
                                                                            <Button
                                                                                variant="primary"
                                                                                size="sm"
                                                                                className="mr-2"
                                                                                onClick={() => {
                                                                                    setCurrentIndex(idx);
                                                                                    setViewMode('preview');
                                                                                }}
                                                                            >
                                                                                <i className="fas fa-eye mr-1" /> Preview
                                                                            </Button>
                                                                            <Button
                                                                                variant="danger"
                                                                                size="sm"
                                                                                onClick={() => onRemoveFromTab(id)}
                                                                            >
                                                                                <i className="fas fa-trash mr-1" /> Xóa
                                                                            </Button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )
                                    ) : (
                                        <>
                                            {/* Row 1: Navigation + Send Email */}
                                            <div className="d-flex align-items-center mb-3">
                                                <Button variant="secondary" className="mr-2" onClick={onClickBack}>
                                                    Back
                                                </Button>
                                                {activeCandidateIdsList.length > 0 && (
                                                    <span className="mr-3">
                                                        {safeIndex + 1} / {activeCandidateIdsList.length}
                                                    </span>
                                                )}
                                                <Button
                                                    variant="secondary"
                                                    className="mr-3"
                                                    disabled={activeCandidateIdsList.length === 0 || safeIndex >= activeCandidateIdsList.length - 1}
                                                    onClick={onClickNext}
                                                >
                                                    Next
                                                </Button>
                                                {activeTab === 'valid' && (
                                                    <>
                                                        <Button
                                                            variant="outline-secondary"
                                                            className="mr-2"
                                                            title="Tải lại thông tin Person"
                                                            aria-label="reload-current-candidate"
                                                            disabled={!currentCandidateId}
                                                            onClick={refreshCurrentCandidate}
                                                        >
                                                            <i className="fas fa-sync-alt" />
                                                        </Button>
                                                        <Button
                                                            variant="success"
                                                            className="mr-3"
                                                            disabled={alreadySentSameBatch}
                                                            title={alreadySentSameBatch
                                                                ? 'Đã gửi campaign cho đúng danh sách email này rồi. Thay đổi lựa chọn (Person/email/checkbox/sender) hoặc tải lại nếu muốn gửi tiếp.'
                                                                : undefined}
                                                            onClick={() => setShowSendTimeModal(true)}
                                                        >
                                                            Send Email
                                                        </Button>
                                                        {/* [SPEC: TEST Gửi Email qua PM - DVTJCUV 5 - BS2] không áp dụng cho
                                                            luồng retry (campaignId có giá trị). */}
                                                        {!campaignId && (
                                                            <div className="form-check mb-0">
                                                                <input type="checkbox" className="form-check-input" id="sendIntroEmailCheckbox"
                                                                       checked={sendIntroEmail} onChange={e => setSendIntroEmail(e.target.checked)} />
                                                                <label className="form-check-label" htmlFor="sendIntroEmailCheckbox">
                                                                    Gửi EMAIL GIỚI THIỆU với Person lạ / quen chưa gth GP trong list
                                                                </label>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>

                                            {/* Row 2: Person info */}
                                            {activeCandidateIdsList.length === 0 ? (
                                                <div className="alert alert-info">Không có person nào trong tab này.</div>
                                            ) : (
                                                <div className="card">
                                                    <div className="card-body">
                                                        {/* Row 2.1: Person summary */}
                                                        <div className="mb-2 d-flex flex-wrap" style={{ gap: '24px' }}>
                                                            <div>
                                                                <strong>Person ID: </strong>
                                                                <a
                                                                    href={`/candidate/detail/${currentCandidate?.general?.id}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {currentCandidate?.general?.id}
                                                                </a>
                                                            </div>
                                                            <div>
                                                                {/* [SPEC: ... DVTJCUV 4: EE9] R5-a */}
                                                                <strong>Tên (email): </strong>
                                                                <strong>{currentCandidate?.personal?.email_name || '-'}</strong>
                                                            </div>
                                                            <div>
                                                                <strong>Displayed Company: </strong>
                                                                {currentCandidate?.general?.displayed_company || '-'}
                                                            </div>
                                                            <div>
                                                                <strong>Displayed Title: </strong>
                                                                {currentCandidate?.general?.displayed_title || '-'}
                                                            </div>
                                                            {/* Quyết định body nào của template được dùng — hiện ra để
                                                                user đối chiếu được nội dung preview với UV lạ/quen. */}
                                                            <div data-testid="candidate-know-state">
                                                                <strong>UV lạ/quen: </strong>
                                                                {knowStateLabel(currentCandidate?.flow_categories?.candidate_know_state) || '-'}
                                                            </div>
                                                        </div>

                                                        {/* Row 2.2: Action buttons */}
                                                        <div className="mb-2">
                                                            <Button
                                                                variant="warning"
                                                                size="sm"
                                                                className="mr-2"
                                                                onClick={() => onExcludeFromSend(currentCandidateId)}
                                                            >
                                                                Loại Person khỏi list gửi email lần này
                                                            </Button>
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                onClick={() => onRemoveFromTab(currentCandidateId)}
                                                            >
                                                                Xóa Person khỏi Tab này
                                                            </Button>
                                                        </div>

                                                        {/* Row 2.3: Email Draft */}
                                                        {currentCandidate && selectedTemplate && (
                                                            <EmailDraftContent
                                                                candidate={currentCandidate}
                                                                selectedTemplate={selectedTemplate}
                                                                selectedEmailsProp={emailSelections[currentCandidateId] || []}
                                                                onEmailListChange={(emails) =>
                                                                    setEmailSelections(prev => ({
                                                                        ...prev,
                                                                        [currentCandidateId]: emails,
                                                                    }))
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            <Modal show={showSendTimeModal} onHide={() => setShowSendTimeModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Chọn thời gian gửi</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-group">
                        <div className="form-check mb-2">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="sendOption"
                                id="sendNow"
                                value="now"
                                checked={sendOption === 'now'}
                                onChange={() => setSendOption('now')}
                            />
                            <label className="form-check-label" htmlFor="sendNow">
                                Gửi ngay
                            </label>
                        </div>
                        <div className="form-check mb-2">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="sendOption"
                                id="sendLater"
                                value="later"
                                checked={sendOption === 'later'}
                                onChange={() => setSendOption('later')}
                            />
                            <label className="form-check-label" htmlFor="sendLater">
                                Gửi sau
                            </label>
                        </div>
                    </div>
                    {sendOption === 'later' && (
                        <div className="form-group mt-3">
                            <label className="d-block">Chọn ngày và giờ:</label>
                            <DatePicker
                                showTimeSelect
                                selected={sendTime}
                                dateFormat="dd/MM/yyyy HH:mm:ss"
                                onChange={(date) => setSendTime(date)}
                                className="form-control"
                            />
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSendTimeModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" disabled={isSending} onClick={onConfirmSend}>
                        {isSending ? 'Đang gửi...' : 'Send'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default DraftPreviewModal;

